'use es6';

import enviro from 'enviro';
import userInfo from 'hub-http/userInfo';
import getLang from 'I18n/utils/getLang';
import PortalIdParser from 'PortalIdParser';
import { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useCanRenderNudge } from 'nudge-client-utils';
import injectScript from 'feedback-loader/utils/injectScript';
import { isAppDomain } from 'feedback-loader/utils/isAppDomain';
import { jsx as _jsx } from "react/jsx-runtime";
const ENV = enviro.getShort();
const qaSuffix = ENV === 'qa' ? 'qa' : '';
const initialPortalId = ENV === 'prod' ? 53 : 100765651;
const getLocalStorageItem = key => {
  try {
    return window.localStorage && window.localStorage.getItem(key);
  } catch (err) {
    console.error(err);
    return null;
  }
};
const loadLocalPopupInjector = getLocalStorageItem('LOCAL_FEEDBACK_UI') === 'true';
const PORTAL_ID_ATTR = 'data-hubspot-feedback-portal-id';
const CUSTOMER_PORTAL_ID_ATTR = 'data-hubspot-feedback-customer-portal-id';
const ENV_ATTR = 'data-hubspot-feedback-env';
const HUBSPOT_APP_ATTR = 'data-hubspot-feedback-hubspot-app';
const HUBSPOT_EMAIL_ATTR = 'data-hubspot-feedback-hubspot-email';
const HUBSPOT_USER_LANG = 'data-hubspot-feedback-user-lang';
const HUBSPOT_CLIENT_LOAD_ONLY = 'data-hubspot-feedback-client-load-only';
const SELENIUM_DISABLE_FEEDBACK_KEY = 'selenium.disable.feedback-surveys';
const hsq = window._hsq = window._hsq || [];
hsq.push(['setCookiePolicy', [{
  enabled: true,
  privacyPolicy: 2,
  privacyDefault: true
}]]);
const addOnClientReadyCallback = onReady => {
  window.onHsFeedbackReady = window.onHsFeedbackReady || [];
  window.onHsFeedbackReady.push(onReady);
};
const inject = (portalId, customerPortalId, userInfoClient, onClientLoad, clientLoadOnly = true, loadAnalyticsScript = true) => {
  const ANALYTICS_SRC = `//js.hubspot${qaSuffix}.com/analytics/0/common.js`;
  const FEEDBACK_INJECTOR_SRC = loadLocalPopupInjector ? `//local.hsappstatic.net/feedback-web-renderer-ui/static/bundles/popupInjector.js` : `//js.hubspotfeedback${qaSuffix}.com/feedbackweb-new.js`;
  if (loadAnalyticsScript) {
    injectScript(ANALYTICS_SRC);
  }
  userInfoClient().then(({
    user,
    hubspotter
  }) => {
    const {
      email
    } = user || hubspotter;
    hsq.push(['identify', {
      email
    }]);
    injectScript(FEEDBACK_INJECTOR_SRC, {
      [PORTAL_ID_ATTR]: portalId,
      [CUSTOMER_PORTAL_ID_ATTR]: customerPortalId,
      [ENV_ATTR]: ENV,
      [HUBSPOT_APP_ATTR]: true,
      [HUBSPOT_EMAIL_ATTR]: email,
      [HUBSPOT_USER_LANG]: getLang(),
      [HUBSPOT_CLIENT_LOAD_ONLY]: clientLoadOnly
    });
    if (onClientLoad) {
      addOnClientReadyCallback(onClientLoad);
    }
  }).catch(console.error);
};
export const FeedbackLoaderContext = /*#__PURE__*/createContext({});
const warnClientNotLoaded = () => {
  console.warn('Client not loaded yet.');
};
const getKey = (id, surveyType) => `${id}-${surveyType}`;
export const FeedbackLoaderProvider = ({
  customerPortalId = PortalIdParser.get(),
  portalId = initialPortalId,
  userInfoClient = userInfo,
  clientLoadOnly = false,
  loadAnalyticsScript = true,
  delayMs = 0,
  checkOrchestrator = true,
  children
}) => {
  const [client, setClient] = useState(null);
  const [config, setConfig] = useState(null);
  const [clientLoaded, setClientLoaded] = useState(false);
  const [surveyCache, setSurveyCache] = useState({});
  const [showPending, setShowPending] = useState(false);
  const originalShowRef = useRef(null);

  // The UEP Orchestrator only ever initializes on app.hubspot(qa).com pages
  // (it's gated behind Whisper's own app-auth check), so on internal/tools
  // pages canRenderNudge() would never resolve and show() would hang
  // forever. Force the check off there regardless of what the consumer
  // passes in.
  const orchestratorCheckEnabled = checkOrchestrator && isAppDomain();

  // Utilize UEP ATC to check for other active nudges
  const {
    canRender
  } = useCanRenderNudge({
    id: 'feedback-survey',
    weight: 3,
    isOpen: showPending
  });
  const onConfigLoaded = useCallback(({
    config: newConfig,
    webRespondent: newWebRespondent
  }) => {
    const key = getKey(newConfig.surveyId, newConfig.surveyType);
    setConfig({
      config: newConfig,
      webRespondent: newWebRespondent
    });
    if (!surveyCache[key]) {
      setSurveyCache(Object.assign({}, surveyCache, {
        [key]: {
          config: newConfig,
          webRespondent: newWebRespondent
        }
      }));
    }
  }, [surveyCache]);
  const onClientLoad = newClient => {
    newClient.onConfigLoaded = onConfigLoaded;
    newClient.onSurveyLoad = onConfigLoaded;
    setClient(newClient);
    setClientLoaded(true);
  };
  useEffect(() => {
    if (clientLoaded) {
      // eslint-disable-next-line react-hooks/immutability
      client.onConfigLoaded = onConfigLoaded;
      // eslint-disable-next-line react-hooks/immutability
      client.onSurveyLoad = onConfigLoaded;
    }
  }, [clientLoaded, onConfigLoaded, client]);

  // eslint-disable-next-line react-hooks/immutability
  useEffect(() => {
    if (!clientLoaded || !client) return undefined;
    const originalShow = client.show.bind(client);
    const originalHide = client.hide.bind(client);
    originalShowRef.current = originalShow;

    // eslint-disable-next-line react-hooks/immutability
    client.show = () => {
      if (orchestratorCheckEnabled) {
        // Add survey to UEP orchestrator waitlist before showing
        setShowPending(true);
      } else {
        originalShow();
      }
    };
    client.hide = () => {
      // Remove survey from UEP orchestrator waitlist
      setShowPending(false);
      originalHide();
    };
    return () => {
      client.show = originalShow;
      client.hide = originalHide;
      originalShowRef.current = null;
    };
  }, [clientLoaded, client, orchestratorCheckEnabled]);
  useEffect(() => {
    // Only render survey if there are no other active nudges
    if (canRender && showPending && originalShowRef.current) {
      originalShowRef.current();
    }
  }, [canRender, showPending]);
  useEffect(() => {
    if (clientLoaded || window.hsFeedbackLoaded || window.disabledHsPopups && window.disabledHsPopups.indexOf('FEEDBACK') > -1 || getLocalStorageItem(SELENIUM_DISABLE_FEEDBACK_KEY) === 'true') {
      return;
    }
    hsq.push(['setPortalId', portalId]);
    setTimeout(() => {
      inject(portalId, customerPortalId, userInfoClient, onClientLoad, clientLoadOnly, loadAnalyticsScript);
    }, delayMs);
    // eslint-disable-next-line
  }, [clientLoaded]);
  const getConfig = useCallback(() => {
    if (clientLoaded) {
      return client.config;
    }
    return warnClientNotLoaded();
  }, [clientLoaded, client]);
  const loadSurvey = useCallback((surveyId, surveyType) => {
    const key = getKey(surveyId, surveyType);
    if (clientLoaded) {
      if (surveyCache[key]) {
        onConfigLoaded(surveyCache[key]);
      } else {
        client.loadSurvey(surveyType, surveyId);
      }
    } else {
      warnClientNotLoaded();
    }
  }, [clientLoaded, surveyCache, onConfigLoaded, client]);
  const hide = useCallback(() => {
    if (clientLoaded) {
      client.hide();
    } else {
      warnClientNotLoaded();
    }
  }, [clientLoaded, client]);
  const show = useCallback(() => {
    if (clientLoaded) {
      client.show();
    } else {
      warnClientNotLoaded();
    }
  }, [clientLoaded, client]);
  const renderSurvey = useCallback(() => {
    if (clientLoaded) {
      client.showSurvey(config);
    } else {
      warnClientNotLoaded();
    }
  }, [clientLoaded, client, config]);
  const loadConfigOnly = useCallback((surveyId, surveyType) => {
    const key = getKey(surveyId, surveyType);
    if (clientLoaded) {
      if (surveyCache[key]) {
        onConfigLoaded(surveyCache[key]);
      } else {
        client.loadConfigOnly(surveyType, surveyId);
      }
    } else {
      warnClientNotLoaded();
    }
  }, [clientLoaded, surveyCache, onConfigLoaded, client]);
  const expand = useCallback(() => {
    if (clientLoaded) {
      client.expand();
    } else {
      warnClientNotLoaded();
    }
  }, [clientLoaded, client]);
  const minimizeSurvey = useCallback(() => {
    if (clientLoaded) {
      client.minimize();
    } else {
      warnClientNotLoaded();
    }
  }, [clientLoaded, client]);
  const getSurveyLanguage = useCallback(() => {
    if (clientLoaded) {
      return client.surveyLanguage;
    }
    return warnClientNotLoaded();
  }, [clientLoaded, client]);
  const getIframe = useCallback(() => {
    if (clientLoaded) {
      return client.iframe;
    }
    return warnClientNotLoaded();
  }, [clientLoaded, client]);
  const getIframeWrapper = useCallback(() => {
    if (clientLoaded) {
      return client.iframeWrapper;
    }
    return warnClientNotLoaded();
  }, [clientLoaded, client]);
  const handleClientLoad = useCallback(newClient => {
    setClient(newClient);
    setClientLoaded(true);
  }, []);
  const value = useMemo(() => ({
    loadSurvey,
    loadConfigOnly,
    renderSurvey,
    getConfig,
    minimizeSurvey,
    expand,
    hide,
    show,
    handleClientLoad,
    config,
    clientLoaded,
    getSurveyLanguage,
    getIframe,
    getIframeWrapper
  }), [loadSurvey, loadConfigOnly, renderSurvey, getConfig, minimizeSurvey, expand, hide, show, handleClientLoad, config, clientLoaded, getSurveyLanguage, getIframe, getIframeWrapper]);
  return /*#__PURE__*/_jsx(FeedbackLoaderContext.Provider, {
    value: value,
    children: children
  });
};